/**
 * engine/note-arrows.ts
 *
 * Note arrows: the player's scratch marks for line solving. An arrow covers a
 * run of cells along one row or column and points one way along it; drawing a
 * line's clues packed to its start (→) and then to its end (←) shows where
 * each clue's two placements overlap. Like cell notes, arrows never touch the
 * board, the clues, or the solve status.
 *
 * Rules: on a line, arrows pointing the same way never overlap (a new arrow
 * takes the place of any it overlaps), so each cell has at most one arrow per
 * direction per axis.
 *
 * Arrows live in history as part of note moves: a notes command's indices
 * past the cell notes (`cellCount` and up) are arrow slots. A slot is one
 * (axis, direction, first cell) and its value says how long the arrow is and
 * its color (0 = no arrow there), so undo/redo, the log, and everything that
 * leaves note moves out of the board (replays, multiplayer) handle arrows too.
 *
 * Lanes: an arrow sits across its cells at a fixed fraction of the cell, in
 * stored orientation (`arrowLane`): forward arrows (→ along a row, ↓ along a
 * column) in the first third, backward ones in the last, so both passes show
 * in the same cells. Pure — no browser, DOM, or Pixi imports.
 */

import type { NoteChanges } from './notes';
import type { LineRef } from './types';
import { cellIndexToRowCol, rowColToCellIndex } from './coordinates';

type LineAxis = LineRef['axis'];

export interface NoteArrow {
  axis: LineAxis;
  /** Row or column index. */
  line: number;
  /** First and last cell along the line (inclusive, start <= end). */
  start: number;
  end: number;
  /** 1 points toward the end of the line (→ / ↓), -1 toward its start. */
  dir: 1 | -1;
  /** Palette color index (1 and up). */
  color: number;
}

/** Arrows are as long as a line is: this many bits hold the color, the rest the length. */
const COLOR_BITS = 5;
const COLOR_MASK = (1 << COLOR_BITS) - 1;
/** Highest color an arrow can have. */
export const MAX_ARROW_COLOR = COLOR_MASK;

/** Where an arrow sits across its cells (0 = top or left edge, 1 = bottom or right), in stored orientation. */
export function arrowLane(dir: 1 | -1): number {
  return dir > 0 ? 0.32 : 0.68;
}

/** Line length for an axis. */
function lineLength(axis: LineAxis, rows: number, columns: number): number {
  return axis === 'row' ? columns : rows;
}

/** Arrow slots come after the cell notes: one block of `cellCount` per (axis, direction). */
function slotOf(arrow: Pick<NoteArrow, 'axis' | 'line' | 'start' | 'dir'>, rows: number, columns: number): number {
  const cellCount = rows * columns;
  const block = (arrow.axis === 'row' ? 0 : 2) + (arrow.dir > 0 ? 0 : 1);
  const cell = arrow.axis === 'row'
    ? rowColToCellIndex(arrow.line, arrow.start, columns)
    : rowColToCellIndex(arrow.start, arrow.line, columns);
  return cellCount * (1 + block) + cell;
}

function encode(arrow: NoteArrow): number {
  return ((arrow.end - arrow.start + 1) << COLOR_BITS) | arrow.color;
}

/** True for indices of a notes command that are arrow slots rather than cell notes. */
export function isArrowSlot(index: number, cellCount: number): boolean {
  return index >= cellCount;
}

/** The arrow a slot value stands for (null for an empty slot or a bad index). */
function decode(slot: number, value: number, rows: number, columns: number): NoteArrow | null {
  const cellCount = rows * columns;
  if (value <= 0 || slot < cellCount || slot >= cellCount * 5) return null;
  const block = Math.floor(slot / cellCount) - 1;
  const cell = slot % cellCount;
  const axis: LineAxis = block < 2 ? 'row' : 'column';
  const dir: 1 | -1 = block % 2 === 0 ? 1 : -1;
  const { row, col } = cellIndexToRowCol(cell, columns);
  const line = axis === 'row' ? row : col;
  const start = axis === 'row' ? col : row;
  const length = value >> COLOR_BITS;
  const color = value & COLOR_MASK;
  const end = start + length - 1;
  if (length < 1 || color < 1 || end >= lineLength(axis, rows, columns)) return null;
  return { axis, line, start, end, dir, color };
}

/** True when an arrow fits the puzzle. */
export function isValidArrow(arrow: NoteArrow, rows: number, columns: number): boolean {
  const count = arrow.axis === 'row' ? rows : columns;
  return (arrow.axis === 'row' || arrow.axis === 'column')
    && Number.isInteger(arrow.line) && arrow.line >= 0 && arrow.line < count
    && Number.isInteger(arrow.start) && Number.isInteger(arrow.end)
    && arrow.start >= 0 && arrow.start <= arrow.end && arrow.end < lineLength(arrow.axis, rows, columns)
    && (arrow.dir === 1 || arrow.dir === -1)
    && Number.isInteger(arrow.color) && arrow.color >= 1 && arrow.color <= MAX_ARROW_COLOR;
}

/** On the same line, pointing the same way, sharing a cell: the pairs that may not both be drawn. */
function overlapsSameWay(a: NoteArrow, b: NoteArrow): boolean {
  return a.axis === b.axis && a.line === b.line && a.dir === b.dir && a.start <= b.end && b.start <= a.end;
}

export function sameArrow(a: NoteArrow, b: NoteArrow): boolean {
  return a.axis === b.axis && a.line === b.line && a.start === b.start && a.end === b.end && a.dir === b.dir && a.color === b.color;
}

/** Note changes to arrow slots: indices are slots, values the slots' new and old contents. */
function changesFor(removed: readonly NoteArrow[], added: NoteArrow[], rows: number, columns: number): NoteChanges | null {
  const slots = new Map<number, { value: number; previous: number }>();
  for (const arrow of removed) slots.set(slotOf(arrow, rows, columns), { value: 0, previous: encode(arrow) });
  for (const arrow of added) {
    const slot = slotOf(arrow, rows, columns);
    const was = slots.get(slot);
    slots.set(slot, { value: encode(arrow), previous: was?.previous ?? 0 });
  }
  const changes: NoteChanges = { indices: [], values: [], previousValues: [] };
  for (const [slot, { value, previous }] of slots) {
    if (value === previous) continue;
    changes.indices.push(slot);
    changes.values.push(value);
    changes.previousValues.push(previous);
  }
  return changes.indices.length > 0 ? changes : null;
}

/**
 * The changes that draw `arrow`: it replaces every arrow on its line that
 * points the same way and overlaps it. Null when nothing changes (the same
 * arrow is already there) or the arrow doesn't fit the puzzle.
 */
export function drawArrowChanges(arrows: readonly NoteArrow[], arrow: NoteArrow, rows: number, columns: number): NoteChanges | null {
  if (!isValidArrow(arrow, rows, columns)) return null;
  if (arrows.some((a) => sameArrow(a, arrow))) return null;
  return changesFor(arrows.filter((a) => overlapsSameWay(a, arrow)), [arrow], rows, columns);
}

/** The changes that erase arrows (those not drawn are skipped), or null when there are none. */
export function eraseArrowChanges(arrows: readonly NoteArrow[], erase: readonly NoteArrow[], rows: number, columns: number): NoteChanges | null {
  const present = erase.filter((e) => arrows.some((a) => sameArrow(a, e)));
  return changesFor(present, [], rows, columns);
}

/** The changes that erase every arrow, or null when there are none. */
export function clearArrowChanges(arrows: readonly NoteArrow[], rows: number, columns: number): NoteChanges | null {
  return changesFor(arrows, [], rows, columns);
}

/** Arrows along a line. */
export function lineArrows(arrows: readonly NoteArrow[], axis: LineAxis, line: number): NoteArrow[] {
  return arrows.filter((a) => a.axis === axis && a.line === line);
}

/** Arrows covering a cell, on either axis. */
export function arrowsThrough(arrows: readonly NoteArrow[], row: number, column: number): NoteArrow[] {
  return arrows.filter((a) => {
    const line = a.axis === 'row' ? row : column;
    const pos = a.axis === 'row' ? column : row;
    return a.line === line && pos >= a.start && pos <= a.end;
  });
}

/**
 * The arrow a tap at `at` inside a cell (fractions of the cell, stored
 * orientation) erases: the only arrow through the cell, or with several, the
 * one whose lane is nearest the tap. Null when no arrow covers the cell.
 */
export function arrowToErase(
  arrows: readonly NoteArrow[],
  cell: { row: number; column: number },
  at?: { x: number; y: number },
): NoteArrow | null {
  const through = arrowsThrough(arrows, cell.row, cell.column);
  if (through.length <= 1 || !at) return through[0] ?? null;
  const distance = (a: NoteArrow) => Math.abs((a.axis === 'row' ? at.y : at.x) - arrowLane(a.dir));
  return through.reduce((best, a) => (distance(a) < distance(best) ? a : best));
}

/**
 * Apply note-command slot changes (`indices` at or past `rows * columns`) to
 * a list of arrows; other indices are skipped. Returns the same list when no
 * slot is touched.
 */
export function applyArrowSlots(
  arrows: readonly NoteArrow[],
  command: { indices: readonly number[]; values: readonly number[] },
  rows: number,
  columns: number,
): readonly NoteArrow[] {
  const cellCount = rows * columns;
  // Most notes commands are cell notes only.
  if (!command.indices.some((slot) => isArrowSlot(slot, cellCount))) return arrows;
  const bySlot = new Map<number, NoteArrow>();
  for (const arrow of arrows) bySlot.set(slotOf(arrow, rows, columns), arrow);
  command.indices.forEach((slot, i) => {
    if (!isArrowSlot(slot, cellCount)) return;
    const arrow = decode(slot, command.values[i], rows, columns);
    if (arrow) bySlot.set(slot, arrow);
    else bySlot.delete(slot);
  });
  return sortArrows([...bySlot.values()]);
}

/** Arrows in a stable order (by axis, line, direction, start). */
function sortArrows(arrows: NoteArrow[]): NoteArrow[] {
  return arrows.sort((a, b) => (a.axis === b.axis ? 0 : a.axis === 'row' ? -1 : 1)
    || a.line - b.line || b.dir - a.dir || a.start - b.start);
}

/**
 * Saved arrows, when usable: each must fit the puzzle, and no two on a line
 * pointing the same way may overlap. Anything else restores no arrows.
 */
export function savedArrows(value: unknown, rows: number, columns: number): NoteArrow[] | null {
  if (!Array.isArray(value)) return null;
  const arrows: NoteArrow[] = [];
  for (const item of value) {
    if (typeof item !== 'object' || item === null) return null;
    const { axis, line, start, end, dir, color } = item as Record<string, unknown>;
    const arrow = { axis, line, start, end, dir, color } as NoteArrow;
    if (!isValidArrow(arrow, rows, columns)) return null;
    if (arrows.some((a) => overlapsSameWay(a, arrow))) return null;
    arrows.push(arrow);
  }
  return sortArrows(arrows);
}
